import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["toggle", "walletNameInput", "form", "shouldSaveInput", "walletNameHiddenInput", "doneButton", "loadingSpinner", "buttonText", "charCount", "currentCount"]
  static values = { maxLength: { type: Number, default: 50 }, shouldSave: { type: Boolean, default: false } }

  connect() {
    if (this.hasToggleTarget) {
      this.toggleTarget.checked = false
      this.toggleTarget.addEventListener("change", this.validateForm.bind(this))
    }

    if (this.hasFormTarget) {
      this.formTarget.addEventListener("submit", this.showLoading.bind(this))
    }

    this.updateCharCount()
    this.validateForm()
  }

  handleInputChange() {
    this.limitInputLength()
    this.updateCharCount()
    this.syncToggle()
    this.validateForm()
  }

  limitInputLength() {
    if (!this.hasWalletNameInputTarget) return

    const value = this.walletNameInputTarget.value
    const maxLength = this.maxLengthValue

    if (value.length > maxLength) {
      this.walletNameInputTarget.value = value.substring(0, maxLength)
    }
  }

  updateCharCount() {
    if (this.hasWalletNameInputTarget && this.hasCurrentCountTarget) {
      this.currentCountTarget.textContent = this.walletNameInputTarget.value.length
    }
  }

  syncToggle() {
    if (this.hasToggleTarget && this.hasWalletNameInputTarget) {
      this.toggleTarget.checked = this.walletNameInputTarget.value.trim().length > 0
    }
  }

  validateForm() {
    if (!this.hasDoneButtonTarget) return

    // Button is always enabled if wallet is already saved or no toggle exists
    const alwaysEnabled = this.shouldSaveValue || !this.hasToggleTarget

    let isValid = alwaysEnabled

    if (!alwaysEnabled && this.hasToggleTarget) {
      const isToggleChecked = this.toggleTarget.checked
      const hasInputValue = this.hasWalletNameInputTarget &&
                           this.walletNameInputTarget.value.trim().length > 0
      // Valid if toggle is off OR (toggle is on AND input has value)
      isValid = !isToggleChecked || hasInputValue

      // Show/hide input based on toggle
      if (this.hasWalletNameInputTarget) {
        const shouldShow = isToggleChecked
        this.walletNameInputTarget.classList.toggle("hidden", !shouldShow)
        if (this.hasCharCountTarget) {
          this.charCountTarget.classList.toggle("hidden", !shouldShow)
        }
      }
    }

    this.doneButtonTarget.disabled = !isValid
    this.doneButtonTarget.classList.toggle("opacity-50", !isValid)
    this.doneButtonTarget.classList.toggle("cursor-not-allowed", !isValid)
    this.doneButtonTarget.classList.toggle("pointer-events-none", !isValid)
    this.doneButtonTarget.classList.toggle("hover:bg-gray-900", isValid)
  }

  handleDoneClick(event) {
    const wantsToSave = this.hasToggleTarget && this.toggleTarget.checked

    if (!wantsToSave) {
      event.preventDefault()
      window.location.href = "/home"
      return
    }

    // Saving - validate and set wallet name
    const walletName = this.hasWalletNameInputTarget
      ? this.walletNameInputTarget.value.trim()
      : ""

    if (!walletName || walletName.length > this.maxLengthValue) {
      event.preventDefault()
      return
    }

    if (this.hasShouldSaveInputTarget) {
      this.shouldSaveInputTarget.value = "true"
    }
    if (this.hasWalletNameHiddenInputTarget) {
      this.walletNameHiddenInputTarget.value = walletName
    }
  }

  showLoading() {
    const isSaving = this.hasToggleTarget && this.toggleTarget.checked

    if (this.hasDoneButtonTarget) {
      this.doneButtonTarget.disabled = true
      this.doneButtonTarget.classList.add("opacity-50", "cursor-not-allowed", "pointer-events-none")
      this.doneButtonTarget.classList.remove("hover:bg-gray-900")
    }

    if (this.hasLoadingSpinnerTarget) {
      this.loadingSpinnerTarget.classList.remove("hidden")
    }

    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = isSaving ? "Saving..." : "Processing..."
    }

    if (this.hasToggleTarget) {
      this.toggleTarget.disabled = true
    }

    if (this.hasWalletNameInputTarget) {
      this.walletNameInputTarget.disabled = true
    }
  }

  showCharCount() {
    if (this.hasCharCountTarget) {
      this.charCountTarget.classList.remove("hidden")
    }
    this.updateCharCount()
  }

  hideCharCount() {
    if (this.hasCharCountTarget && this.hasWalletNameInputTarget) {
      if (this.walletNameInputTarget.value.trim().length === 0) {
        this.charCountTarget.classList.add("hidden")
      }
    }
  }
}

